import type { versionCheckActionType, VersionCheckOptions, VersionData } from 'BreetHelpers';
import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router';

export function useVersionCheck({
	versionFile = '/version.json',
	checkInterval = 6,
	throttleMs = 3,
	onNewVersion,
	versionCheckType = 'polling',
	timeUnit = 'minutes',
	storage = 'local',
}: VersionCheckOptions) {
	const lastEtagRef = useRef<string | null>(null);
	const lastVersionRef = useRef<string | null>(null);
	const prevLocation = useRef<string | null>(null);

	const lastCheckTimeRef = useRef(0);
	const CHECK_INTERVAL = checkInterval * (timeUnit === 'minutes' ? 60 : 1) * 1000;
	const THROTTLE_MS = throttleMs * (timeUnit === 'minutes' ? 60 : 1) * 1000;

	const location = useLocation();

	const getStorage = useCallback((): Storage | null => {
		if (storage === 'local') return window.localStorage;
		if (storage === 'session') return window.sessionStorage;
		return null;
	}, [storage]);

	useEffect(() => {
		const store = getStorage();
		if (store) {
			lastEtagRef.current = store.getItem('app-etag');
			lastVersionRef.current = store.getItem('app-version');
		}
	}, [getStorage]);

	const checkVersion = useCallback(
		async (versionCheckAction: versionCheckActionType) => {
			if (import.meta.env.DEV) {
				return;
			}

			const now = Date.now();
			if (now - lastCheckTimeRef.current < THROTTLE_MS) {
				return;
			}

			lastCheckTimeRef.current = now;

			try {
				const headers: Record<string, string> = {};
				if (lastEtagRef.current) {
					headers['If-None-Match'] = lastEtagRef.current;
				}

				const res = await fetch(versionFile, {
					headers,
					cache: 'no-store',
				});

				if (res.status === 304) return;

				if (!res.ok) return;

				const data = (await res.json()) as VersionData;
				const newBuildTime = data.buildTime ?? '';
				const newEtag = res.headers.get('ETag');

				const oldBuildTime = lastVersionRef.current;

				if (oldBuildTime && newBuildTime !== oldBuildTime) {
					onNewVersion?.(versionCheckAction);
				}

				lastVersionRef.current = newBuildTime;
				lastEtagRef.current = newEtag;

				const store = getStorage();
				if (store) {
					store.setItem('app-version', newBuildTime);
					if (newEtag) store.setItem('app-etag', newEtag);
				}
			} catch (err) {
				console.error('Version check failed:', err);
			}
		},
		[THROTTLE_MS, versionFile, getStorage, onNewVersion]
	);

	useEffect(() => {
		if (versionCheckType !== 'polling') return;

		const intervalId = setInterval(() => {
			if (document.visibilityState === 'visible') {
				checkVersion('prompt-user').catch(console.error);
			}
		}, CHECK_INTERVAL);

		return () => clearInterval(intervalId);
	}, [checkVersion, CHECK_INTERVAL, versionCheckType]);

	useEffect(() => {
		const handler = () => {
			if (document.visibilityState === 'visible') {
				checkVersion('prompt-user').catch(console.error);
			}
		};

		window.addEventListener('focus', handler);
		document.addEventListener('visibilitychange', handler);

		return () => {
			window.removeEventListener('focus', handler);
			document.removeEventListener('visibilitychange', handler);
		};
	}, [checkVersion]);

	useEffect(() => {
		if (versionCheckType !== 'route-change') {
			prevLocation.current = null;
			return;
		}

		const hasPathChanged = location.pathname !== prevLocation.current;

		const shouldRunCheck = prevLocation.current !== null && hasPathChanged;

		if (shouldRunCheck) {
			checkVersion('reload-page').catch(console.error);
		}

		prevLocation.current = location.pathname;
	}, [checkVersion, location.pathname, versionCheckType]);
}
